/**
 * Sign-in + own-profile forms. Mirrors `LoginIdentifier` (Modules/Identity/TenantRules.cs) and `IdentityInput.ValidateProfile`
 * (Modules/Identity/IdentityEndpoints.cs) — change both together.
 */
import { z } from 'zod';
import { MSG, isPhoneVn, normalizePhoneVn, requiredText } from '@/lib/validation';

export const IDENTIFIER_MAX = 256;
export const DISPLAY_NAME_MAX = 200;

/** Same rule as the server: contains "@" → email, otherwise a Vietnamese phone. */
export function identifierKind(v: string): 'email' | 'phone' {
  return v.includes('@') ? 'email' : 'phone';
}

/**
 * Login: an email is only trimmed + lower-cased (no format rule, so no existing account is ever locked out); anything without
 * "@" must be a phone the server can normalize — otherwise it could never match and we say so before the round trip.
 */
export const loginSchema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, 'Nhập email hoặc số điện thoại.')
    .max(IDENTIFIER_MAX, `Tối đa ${IDENTIFIER_MAX} ký tự.`)
    .superRefine((v, ctx) => {
      if (identifierKind(v) === 'phone' && !isPhoneVn(v))
        ctx.addIssue({ code: 'custom', message: 'Email hoặc số điện thoại không hợp lệ.' });
    })
    .transform((v) => (identifierKind(v) === 'email' ? v.toLowerCase() : (normalizePhoneVn(v) ?? v))),
  password: z.string().min(1, 'Nhập mật khẩu.'),
});

/**
 * "Thông tin cá nhân". The phone format applies only when the phone changed (legacy free-text phones stay editable),
 * so the stored phone is passed in. Output phone: normalized "0xxxxxxxxx", the unchanged legacy value, or '' (cleared).
 */
export function profileSchema(storedPhone: string | null | undefined) {
  const stored = (storedPhone ?? '').trim();
  return z.object({
    displayName: requiredText(DISPLAY_NAME_MAX, { label: 'Họ tên' }),
    phone: z
      .string()
      .transform((v) => v.trim())
      .superRefine((v, ctx) => {
        if (v && v !== stored && !isPhoneVn(v)) ctx.addIssue({ code: 'custom', message: MSG.phone() });
      })
      .transform((v) => (v && v !== stored ? (normalizePhoneVn(v) ?? v) : v)),
  });
}
